import React, {ChangeEvent, useState} from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import MenuItem from "@material-ui/core/MenuItem";
import Typography from '@material-ui/core/Typography';
import TextField from "@material-ui/core/TextField";
import Box from '@material-ui/core/Box';
import { RoomType } from "../utils/types";
import { db } from "../utils/firebase";
import { collection, addDoc, updateDoc } from 'firebase/firestore';
import Spinner from "./Spinner";
import Layout from "./Layout";

const useStyles = makeStyles(theme => ({

  title: {
    margin: `${theme.spacing(8)}px 0`
  },
  italic: {
    fontStyle: 'italic'
  },
  spacer: {
    margin: `${theme.spacing(3)}px 0`
  }
}));

type ScaleOptionType = {
  label: string,
  value: string
}

export const scales: ScaleOptionType[] = [
  {
    label: "Fibonacci",
    value: "0, 1, 2, 3, 5, 8, 13, 21, 50, 100, ?"
  },
  {
    label: "Square",
    value: "0, 1, 2, 4, 8, 16, 32, 64, 128, ?"
  },
  {
    label: "Linear",
    value: "0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, ?"
  },
  {
    label: "T-Shirt",
    value: "XS, S, M, L, XL, XXL, ?"
  }
];

const Initial: React.FC = () => {
  const classes  = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [roomSettings, setRoomSettings] = useState<RoomType>({
    id: '',
    title: '',
    scale: scales[0].value,
    users: {}
  });

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: string) =>
    setRoomSettings({...roomSettings, [field]: event.target.value});

  const createRoom = async () => {
    setLoading(true);
    try {
      const roomsRef = collection(db, "rooms");
      const docRef = await addDoc(roomsRef, roomSettings);
      await updateDoc(docRef, { id: docRef.id });
      history.push("/room/" + docRef.id);
    } catch (reason) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Grid container justifyContent="center">
        <Grid item xs={12} className={classes.title}>
          <Typography variant="h2" align="center">Remote estimating made easy</Typography>
          <Typography variant="h4" align="center" className={classes.italic}>Simple. Fun. Effective</Typography>
        </Grid>
        <Grid item xs={10} md={8} lg={6}>
          {!loading && (
            <>
              <Grid item xs={12}>
                <Typography variant="body1" gutterBottom>Set up your session:</Typography>
              </Grid>

              <TextField
                label="Session name"
                variant="outlined"
                value={roomSettings.title}
                onChange={event => handleInputChange(event, 'title')}
                fullWidth
              />

              <TextField
                select
                label="Story points scale"
                value={roomSettings.scale}
                onChange={event => handleInputChange(event, 'scale')}
                margin="normal"
                variant="outlined"
                fullWidth
              >
                {scales.map((option: ScaleOptionType) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label} ({option.value})
                  </MenuItem>
                ))}
              </TextField>

              {error && <Typography>An error occurred while creating the room :(</Typography>}
            </>
          )}

          <Spinner loading={loading} />

          <Grid container justifyContent="center">
            <Box my={2}>
              <Button
                onClick={createRoom}
                disabled={loading}
                color="primary"
                variant="contained"
              >
                Create session
              </Button>
            </Box>
          </Grid>

          <Box display="flex" flexDirection="column" alignItems="center" my={2}>
            <Typography variant="body2">
              <b>or</b> use the
            </Typography>
            
            <Box mt={4} mb={1}>
              <Button
                onClick={() => history.push('/crystal-ball')}
                variant="contained"
              >
                Crystal Ball
              </Button>
            </Box>
            
            <Typography variant="body2" gutterBottom>
              to predict the future using historical velocity or throughput metrics
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Layout>
  )
};

export default Initial;